import React, { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Pin, PinNote } from '@/design-system'
import { StudioArt, StudioPin } from '../../types'
import { STUDIO_ACCENT, monoLabelSx } from '../shared'

export interface StudioCanvasLightboxProps {
  arts: StudioArt[]
  /** Index into `arts` (already filtered — no placeholders). */
  index: number
  roomLabel: string
  galleryTitle: string
  /** Pins for the current print (caller filters by artId, with proximity fallback). */
  pins?: StudioPin[]
  onIndexChange: (index: number) => void
  onClose: () => void
}

const Root = styled(Box)({
  position: 'fixed',
  inset: 0,
  zIndex: 90,
  display: 'flex',
  flexDirection: 'column',
  color: 'var(--ds-night-ink)',
})

const Scrim = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'rgba(4,3,7,0.55)',
  backdropFilter: 'blur(6px) brightness(0.85)',
  WebkitBackdropFilter: 'blur(6px) brightness(0.85)',
})

const Top = styled(Box)({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  flexShrink: 0,
})

const BackBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px 8px 10px',
  borderRadius: 100,
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  fontSize: 12,
  fontWeight: 650,
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
})

const Meta = styled(Box)({ textAlign: 'center', minWidth: 0 })
const MetaTitle = styled(Box)({ fontSize: 12.5, fontWeight: 700 })
const MetaSub = styled(Box)({
  ...monoLabelSx,
  fontSize: 9,
  letterSpacing: '0.1em',
  color: 'rgba(255,255,255,0.45)',
  marginTop: 1,
})

const CloseBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  width: 34,
  height: 34,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#fff',
  fontSize: 15,
  '&:hover': { background: 'rgba(255,255,255,0.12)' },
})

const Stage = styled(Box)({
  position: 'relative',
  zIndex: 2,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 96px 20px',
  minHeight: 0,
})

const NavBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 3,
  width: 44,
  height: 44,
  borderRadius: '50%',
  cursor: 'pointer',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.14)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 18,
  '&:hover': { background: 'rgba(255,255,255,0.16)' },
  '&.prev': { left: 26 },
  '&.next': { right: 26 },
})

const PhotoWrap = styled(Box)({
  position: 'relative',
  maxWidth: 720,
  maxHeight: '100%',
})

const Photo = styled('img')({
  display: 'block',
  maxWidth: '100%',
  maxHeight: '68vh',
  borderRadius: 4,
  boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
  background: 'rgba(244,243,246,0.04)',
})

const Filmstrip = styled(Box)({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  gap: 8,
  // Top pad so the active thumb's lift + gold ring isn't clipped by
  // overflow-x creating a scrollport (overflow-y becomes auto-clip).
  padding: '8px 20px 22px',
  flexShrink: 0,
  overflowX: 'auto',
  overflowY: 'visible',
})

const Thumb = styled('button', { shouldForwardProp: (p) => p !== '$active' })<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  width: 52,
  height: 52,
  borderRadius: 8,
  overflow: 'hidden',
  cursor: 'pointer',
  flexShrink: 0,
  // Transparent ring keeps layout stable; active uses outline so the
  // gold stroke doesn't eat into the image (border-box + 2px border was
  // shrinking the photo) and doesn't get clipped by a tight parent.
  border: '2px solid transparent',
  outline: $active ? `2px solid ${STUDIO_ACCENT.gold}` : 'none',
  outlineOffset: 0,
  opacity: $active ? 1 : 0.55,
  transform: $active ? 'translateY(-3px)' : 'none',
  transition: 'opacity 140ms, outline-color 140ms, transform 140ms',
  '&:hover': { opacity: 0.85 },
  '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
}))

/**
 * In-canvas lightbox — room stays mounted underneath (dimmed via scrim).
 */
export function StudioCanvasLightbox({
  arts,
  index,
  roomLabel,
  galleryTitle,
  pins = [],
  onIndexChange,
  onClose,
}: StudioCanvasLightboxProps) {
  const [pinOpen, setPinOpen] = useState(false)
  const count = arts.length
  const art = arts[index] ?? null
  const pin = pins[0] ?? null

  useEffect(() => {
    setPinOpen(false)
  }, [index])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (!count) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onIndexChange((index - 1 + count) % count)
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        onIndexChange((index + 1) % count)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [count, index, onClose, onIndexChange])

  const metaSub = useMemo(
    () => `${roomLabel.toUpperCase()}${galleryTitle ? ` · ${galleryTitle.toUpperCase()}` : ''}`,
    [roomLabel, galleryTitle],
  )

  if (!art || count === 0) return null

  return (
    <Root role='dialog' aria-modal aria-label='Print lightbox'>
      <Scrim onClick={onClose} />
      <Top>
        <BackBtn type='button' onClick={onClose}>
          ← Back to room
        </BackBtn>
        <Meta>
          <MetaTitle>
            {art.name || `Print ${index + 1}`} · {index + 1} of {count}
          </MetaTitle>
          <MetaSub>{metaSub}</MetaSub>
        </Meta>
        <CloseBtn type='button' aria-label='Close' onClick={onClose}>
          ✕
        </CloseBtn>
      </Top>

      <Stage>
        {count > 1 && (
          <NavBtn type='button' className='prev' aria-label='Previous print' onClick={() => onIndexChange((index - 1 + count) % count)}>
            ‹
          </NavBtn>
        )}
        <PhotoWrap>
          <Photo src={art.img} alt={art.name || 'Print'} />
          {pin && (
            <>
              <Pin
                tone={pin.tone}
                label={pin.authorInitial}
                anchor={{ left: '22%', top: '30%' }}
                onClick={() => setPinOpen((v) => !v)}
                sx={{ cursor: 'pointer', zIndex: 2 }}
              />
              {pinOpen && (
                <PinNote
                  avatarLabel={pin.authorInitial}
                  name={pin.authorName}
                  note={pin.text}
                  anchor={{ left: '30%', top: '26%' }}
                  sx={{ zIndex: 2 }}
                />
              )}
            </>
          )}
        </PhotoWrap>
        {count > 1 && (
          <NavBtn type='button' className='next' aria-label='Next print' onClick={() => onIndexChange((index + 1) % count)}>
            ›
          </NavBtn>
        )}
      </Stage>

      {count > 1 && (
        <Filmstrip>
          {arts.map((a, i) => (
            <Thumb key={a.id} type='button' $active={i === index} onClick={() => onIndexChange(i)} title={a.name}>
              <img src={a.img} alt='' />
            </Thumb>
          ))}
        </Filmstrip>
      )}
    </Root>
  )
}
